import type { QueryClient } from '@tanstack/react-query';

/**
 * Patient-page caches. Removing these on a clinic switch drops the previous
 * clinic's record so a later visit cannot paint it under the new clinic.
 * Portal caches (`portal`, …) are not included.
 */
const PATIENT_SCOPED_QUERY_PREFIXES: readonly (readonly string[])[] = [
  ['patients', 'detail'],
  ['patients', 'clinic-id'],
  ['patients', 'age'],
  ['patient-brief'],
  ['appointments', 'patient'],
  ['invoices', 'patient'],
  ['tasks', 'patient'],
  ['punch-cards', 'patient'],
  ['clinicForms', 'assignments'],
  ['patient-staff-assignments'],
  ['messages', 'patient'],
  ['patient-relations'],
  ['referrals', 'letters-by-patient'],
  ['referrals', 'id-by-patient'],
  ['consent-records'],
  ['patient-consent-current'],
  ['consent-requests'],
  ['communication-preference'],
  ['journal', 'entries'],
];

export function resetPatientScopedQueries(queryClient: QueryClient): void {
  for (const queryKey of PATIENT_SCOPED_QUERY_PREFIXES) {
    queryClient.removeQueries({ queryKey: [...queryKey] });
  }
}

const PATIENT_DETAIL =
  /^\/patients\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\/|$)/i;

/**
 * Where to go when the active clinic changes while a single clinic record is open.
 * List pages return null so they stay put and reload for the new clinic.
 */
export function listPathForClinicScopedDetail(pathname: string): string | null {
  const path = pathname.split('?')[0] ?? pathname;
  if (PATIENT_DETAIL.test(path)) return '/patients';
  if (/^\/invoices\/(?!new(?:\/|$))[^/]+/.test(path)) return '/economy/invoices';
  if (/^\/quotes\/(?!new(?:\/|$))[^/]+/.test(path)) return '/economy/quotes';
  if (/^\/henvisninger\/(?!new(?:\/|$))[^/]+/.test(path)) return '/henvisninger';
  if (/^\/medcom\/letters\/[^/]+/.test(path)) return '/medcom/letters';
  return null;
}
